import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Search, X, User, Leaf, ChefHat, Star, Clock, ShoppingCart, Tag, Phone } from "lucide-react";

import { getMenuCategories, getMenuProducts } from "../api/restaurant/menuApi";
import OrderModal from "../components/restaurant/OrderModal";
import CartOffcanvas from "../components/restaurant/CartOffcanvas";
import DishModal from "../components/restaurant/DishModal";
import { useThemeStore } from "../store/themeStore";
import { useAuthStore } from '@shared/store/authStore';

// --- Helpers ---
function currency(n: number) {
  return new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB", maximumFractionDigits: 0 }).format(n);
}

// Working Hours Component
function WorkingHours() {
  const [isExpanded, setIsExpanded] = useState(false);

  const schedule = [
    { day: 'Понедельник', hours: '09:00 − 20:40' },
    { day: 'Вторник', hours: '09:00 − 20:40' },
    { day: 'Среда', hours: '09:00 − 20:40' },
    { day: 'Четверг', hours: '09:00 − 20:40' },
    { day: 'Пятница', hours: '09:00 − 22:40' },
    { day: 'Суббота', hours: '09:00 − 22:40' },
    { day: 'Воскресенье', hours: '10:00 − 20:40' }
  ];

  const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
  const todayIndex = today === 0 ? 6 : today - 1; // Convert to our schedule array index
  const todaySchedule = schedule[todayIndex];

  const currentTime = new Date();
  const isOpen = () => {
    const [startHour, startMin] = todaySchedule.hours.split(' − ')[0].split(':');
    const [endHour, endMin] = todaySchedule.hours.split(' − ')[1].split(':');

    const start = new Date();
    start.setHours(parseInt(startHour), parseInt(startMin), 0, 0);

    const end = new Date();
    end.setHours(parseInt(endHour), parseInt(endMin), 0, 0);

    return currentTime >= start && currentTime <= end;
  };

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#525252'
        }}
      >
        <span style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: isOpen() ? '#22c55e' : '#ef4444'
        }}></span>
        <span>
          Сегодня: {todaySchedule.hours} {isOpen() ? '(Открыто)' : '(Закрыто)'}
        </span>
        <span style={{
          fontSize: '12px',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }}>
          ▼
        </span>
      </button>

      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: '1px solid #e5e5e5',
            overflow: 'hidden'
          }}
        >
          {schedule.map((item, index) => (
            <div
              key={item.day}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '4px',
                padding: '2px 0',
                backgroundColor: index === todayIndex ? '#f0f9ff' : 'transparent',
                borderRadius: '4px',
                paddingLeft: index === todayIndex ? '8px' : '0',
                paddingRight: index === todayIndex ? '8px' : '0',
                fontWeight: index === todayIndex ? '600' : '400'
              }}
            >
              <span style={{ color: '#525252', fontSize: '14px' }}>{item.day}:</span>
              <span style={{ color: '#525252', fontSize: '14px' }}>{item.hours}</span>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}

export default function RestaurantPage() {
  const { isDark, toggleTheme } = useThemeStore();
  const { user, isAuthenticated } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [popularDishes, setPopularDishes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<Record<string, any>>({}); // number | { productId, quantity, modifiers }
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedDish, setSelectedDish] = useState<any>(null);
  const [dishModalOpen, setDishModalOpen] = useState(false);
  const [modifiersByScheme, setModifiersByScheme] = useState<Record<string, any[]>>({});
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsCacheMap, setProductsCacheMap] = useState<Record<string, { products: any[]; modifiersByScheme: any }>>({});

  // Функции корзины
  const addToCart = (id: string, quantity: number = 1, modifiers: any[] = []) => {
    setCartItems((s) => {
      let updated;
      if (modifiers.length > 0) {
        const modifierKey = modifiers.map((m: any) => `${m.id}:${m.count}`).sort().join(',');
        const uniqueId = `${id}_${modifierKey}`;
        const existing = s[uniqueId];
        updated = {
          ...s,
          [uniqueId]: {
            productId: id,
            quantity: (existing?.quantity || 0) + quantity,
            modifiers
          }
        };
      } else {
        const current = typeof s[id] === 'number' ? s[id] : 0;
        updated = { ...s, [id]: current + quantity };
      }
      localStorage.setItem('restaurant-cart', JSON.stringify(updated));
      window.dispatchEvent(new CustomEvent('restaurantCartUpdated'));
      return updated;
    });
  };
  const removeFromCart = (key: string) => setCartItems((s) => {
    const next = { ...s };
    const val = next[key];
    if (val === undefined) return s;
    if (typeof val === 'number') {
      next[key] = val - 1;
      if (next[key] <= 0) delete next[key];
    } else {
      if (val.quantity <= 1) {
        delete next[key];
      } else {
        next[key] = { ...val, quantity: val.quantity - 1 };
      }
    }
    localStorage.setItem('restaurant-cart', JSON.stringify(next));
    window.dispatchEvent(new CustomEvent('restaurantCartUpdated'));
    return next;
  });
  const clearCart = () => {
    setCartItems({});
    localStorage.removeItem('restaurant-cart');
    // Notify TopBar about cart update
    window.dispatchEvent(new CustomEvent('restaurantCartUpdated'));
  };

  const getProductById = (id: string) => {
    // Ищем в текущих продуктах
    const found = products.find((product: any) => product.id === id);
    if (found) return found;
    // Ищем во всех закэшированных категориях (для корзины)
    for (const cached of Object.values(productsCacheMap)) {
      const p = cached.products.find((product: any) => product.id === id);
      if (p) return p;
    }
    return null;
  };

  // Получение статистики популярного блюда
  const getPopularDishStats = (productId: string) => {
    return popularDishes.find((dish: any) => dish.id === productId) || null;
  };

  // Загрузка корзины из localStorage при монтировании
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('restaurant-cart');
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      }
    } catch (error) {
    }

    // Обработчик события открытия корзины из TopBar
    const handleOpenCart = () => {
      setCartOpen(true);
    };

    window.addEventListener('openRestaurantCart', handleOpenCart);

    return () => {
      window.removeEventListener('openRestaurantCart', handleOpenCart);
    };
  }, []);

  const cartEntries = Object.entries(cartItems).map(([key, value]) => {
    if (typeof value === 'number') {
      return { product: getProductById(key), qty: value, modifiers: [] as any[], cartKey: key };
    } else {
      return { product: getProductById(value.productId), qty: value.quantity, modifiers: value.modifiers || [], cartKey: key };
    }
  }).filter(({ product }) => product);

  const cartSubtotal = cartEntries.reduce((sum, { product, qty, modifiers }) => {
    const modPrice = modifiers.reduce((s: number, m: any) => s + (m.price || 0) * (m.count || 1), 0);
    return sum + ((product?.price || 0) + modPrice) * qty;
  }, 0);

  // Загрузка данных через API
  // Шаг 1: загрузка категорий (быстро)
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getMenuCategories();
        if (result.success && result.categories) {
          setCategories(result.categories);
          if (result.categories.length > 0 && !selectedCategory) {
            setSelectedCategory(result.categories[0].id);
          }
        } else {
          setError(result.error || 'Не удалось загрузить категории');
        }
      } catch (error) {
        setError('Ошибка загрузки категорий');
      } finally {
        setLoading(false);
      }
    };
    loadCategories();
  }, []);

  // Шаг 2: загрузка продуктов при выборе категории
  useEffect(() => {
    if (!selectedCategory) return;

    // Проверяем фронт-кэш
    if (productsCacheMap[selectedCategory]) {
      const cached = productsCacheMap[selectedCategory];
      setProducts(cached.products);
      setModifiersByScheme(prev => ({ ...prev, ...cached.modifiersByScheme }));
      return;
    }

    const loadProducts = async () => {
      try {
        setProductsLoading(true);
        const result = await getMenuProducts(selectedCategory);
        if (result.success && result.products) {
          setProducts(result.products);
          if (result.modifiersByScheme) {
            setModifiersByScheme(prev => ({ ...prev, ...result.modifiersByScheme }));
          }
          // Сохраняем в фронт-кэш
          setProductsCacheMap(prev => ({
            ...prev,
            [selectedCategory]: { products: result.products, modifiersByScheme: result.modifiersByScheme || {} }
          }));
        }
      } catch (error) {
        console.error('Ошибка загрузки продуктов:', error);
      } finally {
        setProductsLoading(false);
      }
    };
    loadProducts();
  }, [selectedCategory]);

  // Фильтрация продуктов (уже для выбранной категории, фильтруем только по поиску)
  const filtered = useMemo(() => {
    let filteredProducts = products;

    // Фильтр по поиску
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredProducts = filteredProducts.filter((product: any) =>
        product.name.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query)
      );
    }

    return filteredProducts;
  }, [products, searchQuery]);

  // Обработчики
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  // Конвертация для CartOffcanvas
  const cartItemsForOffcanvas = cartEntries.map(({ product, qty, modifiers, cartKey }) => ({
    ...product,
    quantity: qty,
    modifiers,
    cartKey
  }));

  const handleOrderComplete = () => {
    setCartItems({});
    localStorage.removeItem('restaurant-cart');
    // Notify TopBar about cart update
    window.dispatchEvent(new CustomEvent('restaurantCartUpdated'));
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
      <style>{`
        .category-scroll::-webkit-scrollbar {
          display: none;
        }

        .logo-section-unified {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          border-bottom: 1px solid #f0f0f0;
        }

        .header-nav-section {
          padding: 12px 16px;
          display: flex;
          justify-content: center;
        }

        .desktop-logo {
          height: 48px;
        }

        @media (max-width: 768px) {
          .logo-section-unified {
            padding: 12px 16px !important;
            justify-content: space-between !important;
          }

          .category-scroll {
            justify-content: flex-start !important;
          }
        }

        @media (min-width: 769px) {
          .mobile-only {
            display: none !important;
          }
        }
      `}</style>
      {/* Categories Section */}
      <div style={{ position: 'sticky', top: '80px', zIndex: 40, backdropFilter: 'blur(8px)', backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ maxWidth: '1152px', margin: '0 auto' }}>
          <div className="header-nav-section">
            <nav style={{
              width: '100%',
              overflow: 'hidden'
            }}>
              <div className="category-scroll" style={{
                display: 'flex',
                gap: '8px',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                paddingBottom: '2px',
                justifyContent: 'flex-start',
                paddingLeft: '16px',
                paddingRight: '16px'
              }}>
                {categories.map((category: any, index: number) => (
                  <button
                    key={`category-desktop-${category.id}-${index}`}
                    onClick={() => handleCategoryChange(category.id)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '9999px',
                      fontSize: '14px',
                      backgroundColor: selectedCategory === category.id ? '#000' : '#f5f5f5',
                      color: selectedCategory === category.id ? '#fff' : '#000',
                      whiteSpace: 'nowrap',
                      border: 'none',
                      cursor: 'pointer',
                      flexShrink: 0,
                      fontWeight: '500'
                    }}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </nav>
          </div>
        </div>
      </div>


      {/* Product grid */}
      <main style={{ maxWidth: '1152px', margin: '0 auto', padding: '24px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <div style={{ fontSize: '24px', marginBottom: '16px' }}>🍽️</div>
            <p>Загружаем меню...</p>
          </div>
        ) : productsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p>Загружаем блюда...</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#ef4444', backgroundColor: '#fef2f2', borderRadius: '12px' }}>
            ⚠️ {error}
          </div>
        ) : (
          <>
            {/* Продукты */}
            {(() => {
              return filtered.length > 0;
            })() ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
                {filtered.map((product: any, index: number) => (
                  <div key={`product-${product.id}-${index}`} style={{ backgroundColor: 'var(--card-bg)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow)', transition: 'transform 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                    {/* Изображение */}
                    <div style={{ height: '220px', overflow: 'hidden' }}>
                      {product.imageUrls && product.imageUrls.length > 0 ? (
                        <img
                          src={product.imageUrls[0]}
                          alt={product.name}
                          loading="lazy"
                          style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-tertiary)', color: '#9ca3af' }}>
                          <ChefHat size={48} />
                        </div>
                      )}
                    </div>

                    {/* Информация */}
                    <div style={{ padding: '20px' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', lineHeight: '1.3' }}>
                        {product.name}
                      </h3>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px', lineHeight: '1.4' }}>
                        {product.description}
                      </p>

                      {/* Детали */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                        {product.measure && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Leaf size={16} />
                            {product.measure.value} {product.measure.unit}
                          </span>
                        )}
                        {product.calories && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Star size={16} />
                            {product.calories} ккал
                          </span>
                        )}
                      </div>


                      {/* Цена и кнопка */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
                          {product.price} ₽
                        </div>
                        {product.modiSchemeId ? (
                          /* Блюдо с модификаторами — всегда кнопка "+" → модалка */
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedDish(product);
                              setDishModalOpen(true);
                            }}
                            style={{
                              backgroundColor: '#000',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '12px',
                              padding: '12px 16px',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s',
                              position: 'relative',
                              zIndex: 10
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#000'}
                          >
                            <Plus size={20} />
                          </button>
                        ) : cartItems[product.id] ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                removeFromCart(product.id);
                              }}
                              style={{
                                backgroundColor: '#f5f5f5',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '8px',
                                cursor: 'pointer',
                                position: 'relative',
                                zIndex: 10
                              }}
                            >
                              <Minus size={16} />
                            </button>
                            <span style={{ fontWeight: '600' }}>{cartItems[product.id]}</span>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                addToCart(product.id);
                              }}
                              style={{
                                backgroundColor: '#000',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '8px',
                                cursor: 'pointer',
                                position: 'relative',
                                zIndex: 10
                              }}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              addToCart(product.id);
                            }}
                            style={{
                              backgroundColor: '#000',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '12px',
                              padding: '12px 16px',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s',
                              position: 'relative',
                              zIndex: 10
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#000'}
                          >
                            <Plus size={20} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🍽️</div>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>Блюда не найдены</h3>
                <p style={{ color: '#6b7280' }}>
                  {searchQuery ? 'Попробуйте изменить поисковый запрос' : 'Выберите другую категорию'}
                </p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Order Modal */}
      <OrderModal
        isOpen={orderModalOpen}
        onClose={() => {
          setOrderModalOpen(false);
          setCartOpen(false); // Закрываем корзину после успешного заказа
        }}
        cartItems={cartItemsForOffcanvas}
        user={user}
        onOrderComplete={handleOrderComplete}
      />

      {/* Dish Modal (modifiers) */}
      <DishModal
        isOpen={dishModalOpen}
        onClose={() => setDishModalOpen(false)}
        dish={selectedDish}
        modifiers={selectedDish?.modiSchemeId ? (modifiersByScheme[selectedDish.modiSchemeId] || []) : []}
        onAddToCart={(id, quantity, modifiers) => {
          addToCart(id, quantity, modifiers);
        }}
      />

      {/* Cart Offcanvas */}
      <CartOffcanvas
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        cartItems={cartItemsForOffcanvas}
        cartEntries={Object.values(cartItems).reduce((a: number, val: any) => a + (typeof val === 'number' ? val : val.quantity), 0)}
        cartSubtotal={cartSubtotal}
        addToCart={(item) => {
          if (item.modifiers?.length > 0) {
            addToCart(item.id, 1, item.modifiers);
          } else {
            addToCart(item.id);
          }
        }}
        removeFromCart={(key) => removeFromCart(key)}
        clearCart={clearCart}
        onOrderClick={() => setOrderModalOpen(true)}
      />

    </div>
  );
}